import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AdminPermission, AdminRole } from '@onov/contracts';

/**
 * Admin session. Two-step: password → token (mfaVerified=false) → TOTP →
 * token (mfaVerified=true). No admin route accepts a pre-MFA token
 * (AC-ADM-01.3), so the shell routes to /mfa until verified. Persisted in
 * sessionStorage only — closing the tab ends the session (AC-ADMGL-10 spirit).
 */
interface AuthState extends PersistedAuth {
  signIn: (t: {
    accessToken: string;
    role: AdminRole | 'custom';
    roleName: string;
    permissions: readonly string[];
    mfaVerified: boolean;
    email: string;
  }) => void;
  signOut: () => void;
}

interface PersistedAuth {
  token: string | null;
  /** `'custom'` when the session holds a custom role; `roleName` names it. */
  role: AdminRole | 'custom' | null;
  roleName: string | null;
  /**
   * The session's permission set from the server. Permissions are the only
   * authorization input for navigation and route gates — role names are
   * display. The API re-derives this per request, so it is a rendering hint,
   * never a control (AC-ADMGL-01).
   */
  permissions: readonly string[];
  mfaVerified: boolean;
  email: string | null;
}

const SIGNED_OUT: PersistedAuth = {
  token: null,
  role: null,
  roleName: null,
  permissions: [],
  mfaVerified: false,
  email: null,
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      ...SIGNED_OUT,
      signIn: (t) =>
        set({
          token: t.accessToken,
          role: t.role,
          roleName: t.roleName,
          // The one place server data becomes an authorization input for
          // rendering. An API that predates permissions (or any malformed
          // payload) must degrade to "no powers", never to a crashed shell.
          permissions: Array.isArray(t.permissions) ? t.permissions : [],
          mfaVerified: t.mfaVerified,
          email: t.email,
        }),
      signOut: () => set({ ...SIGNED_OUT }),
    }),
    {
      name: 'onov-admin-session',
      version: 2,
      partialize: (s): PersistedAuth => ({
        token: s.token,
        role: s.role,
        roleName: s.roleName,
        permissions: s.permissions,
        mfaVerified: s.mfaVerified,
        email: s.email,
      }),
      /**
       * v1 sessions carry a token and a role name but no permission set, so
       * they would render a shell with nothing in it. Drop them and ask for a
       * fresh sign-in — fail closed, one refresh.
       */
      migrate: () => ({ ...SIGNED_OUT }),
      storage: {
        getItem: (k) => {
          const v = sessionStorage.getItem(k);
          return v ? JSON.parse(v) : null;
        },
        setItem: (k, v) => sessionStorage.setItem(k, JSON.stringify(v)),
        removeItem: (k) => sessionStorage.removeItem(k),
      },
    },
  ),
);

/**
 * Permission check for rendering convenience — hiding an action the caller
 * cannot perform. Never a control: the API re-checks every call and answers
 * 403 regardless of what the console chose to draw (AC-ADMGL-01).
 */
export const useCan = (): ((permission: AdminPermission) => boolean) => {
  const permissions = useAuthStore((s) => s.permissions);
  return (permission) => permissions.includes(permission);
};
